import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { authApi } from '../api/authApi';
import { useAuthStore } from '../store/authStore';
import type { LoginRequest } from '../types/auth';

export const useAuth = () => {
  const setUser = useAuthStore((s) => s.setUser);
  const reset = useAuthStore((s) => s.reset);
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (meQuery.isSuccess) setUser(meQuery.data);
    if (meQuery.isError) reset();
  }, [meQuery.isSuccess, meQuery.isError, meQuery.data, setUser, reset]);

  const loginMutation = useMutation({
    mutationFn: (req: LoginRequest) => authApi.login(req),
    onSuccess: (data) => {
      setUser(data);
      queryClient.setQueryData(['auth', 'me'], data);
      enqueueSnackbar('Přihlášení úspěšné', { variant: 'success' });
      const target =
        data.role === 'SUPER_ADMIN'
          ? '/superadmin'
          : data.role === 'UNIT_ADMIN'
            ? '/admin'
            : '/recorder';
      navigate(target);
    },
    onError: () => {
      enqueueSnackbar('Neplatné přihlašovací údaje', { variant: 'error' });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      reset();
      queryClient.clear();
      navigate('/login');
    },
  });

  return {
    user,
    loading: loading || meQuery.isLoading,
    isAuthenticated: !!user,
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
  };
};
